// ---------------------------------------------------------------------------
// Bulk edit — appears when tasks are selected (ctrl/cmd-click cards, table
// checkboxes). Fans the existing single-task mutations across the selection:
// move stage, add assignee, shift dates ±N days, complete, clear.
//
// Safety model: NO confirm dialogs. Confirms ask you to predict a mistake;
// Undo lets you fix one. Before any bulk write fires we snapshot each task's
// mutable fields, and the result toast — protected, like the single-card ✓'s
// (toastStore {protect:true}) — carries an Undo that replays the inverse
// patches through the same worker queue. The riskiest ops in the app must not
// have LESS protection than a single card's checkmark.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { Check, Minus, Plus } from 'lucide-react';
import type { Stage, TaskListItem } from '@cg-projects/shared';
import { Icon } from '@/core/ui/icons';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { useToastStore } from '@/core/ui/toastStore';
import { moveStage, patchTask } from '@/features/tasks/mutations';

function shiftIso(iso: string, days: number): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** One bulk operation on one task: the write, and the write that reverses it.
 *  `undo` closes over a snapshot taken BEFORE `run` fires — the selected
 *  TaskListItems are immutable store rows, so reading them at build time is
 *  guaranteed pre-write. */
interface BulkOp {
  run: () => Promise<boolean>;
  undo: () => Promise<boolean>;
}

/** The Undo material: every field a bulk op can touch, as it was pre-write. */
function snapshotOf(t: TaskListItem) {
  return {
    stageId: t.stageId,
    assigneeIds: t.assignees.map((a) => a.id),
    assignees: t.assignees,
    deadline: t.deadline,
    filmDate: t.filmDate,
    state: t.state,
  };
}

export function BulkBar({ areaId }: { areaId: number }) {
  const selectedIds = useTasksStore((s) => s.selectedIds);
  const clearSelected = useTasksStore((s) => s.clearSelected);
  const tasks = useTasksStore((s) => s.tasksByArea[areaId]);
  const stages = useAreaStore((s) => s.stagesByArea[areaId]) as Stage[] | undefined;
  const assignees = useLookupStore((s) => s.assignees);
  const toast = useToastStore((s) => s.show);
  const [busy, setBusy] = useState(false);
  const [shiftDays, setShiftDays] = useState(7);

  if (selectedIds.length === 0) return null;
  const selected: TaskListItem[] = (tasks ?? []).filter((t) => selectedIds.includes(t.id));
  if (selected.length === 0) return null; // everything selected has since left the board

  // Replay the inverse patches through the same 3-worker queue. Single-fire:
  // clicking Undo clears the toast (Toast.tsx runs the action, then clear()),
  // so the replay can never be triggered twice.
  const undoAll = async (undos: Array<() => Promise<boolean>>) => {
    setBusy(true); // harmless no-op if the bar unmounted after clearSelected
    let okCount = 0;
    const queue = [...undos];
    const workers = Array.from({ length: 3 }, async () => {
      for (let op = queue.shift(); op; op = queue.shift()) {
        if (await op()) okCount += 1;
      }
    });
    await Promise.all(workers);
    setBusy(false);
    toast(`Undo: ${okCount}/${undos.length} task${undos.length === 1 ? '' : 's'} restored.`, okCount === undos.length ? 'info' : 'error');
  };

  // Fan out with small concurrency so 30 selections don't hammer Odoo at once.
  const runAll = async (label: string, ops: BulkOp[]) => {
    if (busy) return;
    if (!ops.length) {
      // Silence here reads as "the app is broken" — say why nothing happened.
      toast('Nothing to change — the selected tasks are already there (or have no dates).', 'info');
      return;
    }
    setBusy(true);
    // Collect the inverse of each write that actually LANDED — a failed write
    // changed nothing, so it has nothing to reverse.
    const undos: Array<() => Promise<boolean>> = [];
    const queue = [...ops];
    const workers = Array.from({ length: 3 }, async () => {
      for (let op = queue.shift(); op; op = queue.shift()) {
        if (await op.run()) undos.push(op.undo);
      }
    });
    await Promise.all(workers);
    setBusy(false);
    const okCount = undos.length;
    // Partial-failure honesty stays: report exactly how many landed. Protected
    // like the single-task ✓ — a routine notification toast must not displace
    // the only way back from a 30-task write.
    toast(
      `${label}: ${okCount}/${ops.length} task${ops.length === 1 ? '' : 's'} updated.`,
      okCount === ops.length ? 'info' : 'error',
      okCount > 0 ? { label: 'Undo', undo: true, run: () => void undoAll(undos) } : undefined,
      okCount > 0 ? { protect: true } : undefined,
    );
    if (okCount === ops.length) clearSelected();
  };

  const bulkStage = (stageId: number) =>
    runAll('Moved', selected
      .filter((t) => t.stageId !== stageId)
      .map((t): BulkOp => {
        const snap = snapshotOf(t);
        return {
          run: () => moveStage(t.id, stageId),
          // Move back = the single-card drag's own undo: the server re-opens a
          // task the target column auto-closed, restoring column AND state.
          undo: () => moveStage(t.id, snap.stageId),
        };
      }));

  const bulkAssign = (uid: number) => {
    const person = assignees.find((a) => a.id === uid);
    if (!person) return;
    void runAll(`Assigned ${person.name}`, selected
      .filter((t) => !t.assignees.some((a) => a.id === uid))
      .map((t): BulkOp => {
        const snap = snapshotOf(t);
        return {
          run: () => patchTask(t.id, { assigneeIds: [...snap.assigneeIds, uid] }, { assignees: [...snap.assignees, person] }),
          undo: () => patchTask(t.id, { assigneeIds: snap.assigneeIds }, { assignees: snap.assignees }),
        };
      }));
  };

  const bulkShift = (days: number) => {
    if (!days) return;
    void runAll(`Shifted ${days > 0 ? '+' : ''}${days}d`, selected
      .filter((t) => t.deadline || t.filmDate)
      .map((t): BulkOp => {
        const snap = snapshotOf(t);
        const input: { deadline?: string; filmDate?: string } = {};
        const opt: Partial<TaskListItem> = {};
        const undoInput: { deadline?: string; filmDate?: string } = {};
        const undoOpt: Partial<TaskListItem> = {};
        if (snap.deadline) {
          input.deadline = shiftIso(snap.deadline, days); opt.deadline = input.deadline;
          undoInput.deadline = snap.deadline; undoOpt.deadline = snap.deadline;
        }
        if (snap.filmDate) {
          input.filmDate = shiftIso(snap.filmDate, days); opt.filmDate = input.filmDate;
          undoInput.filmDate = snap.filmDate; undoOpt.filmDate = snap.filmDate;
        }
        return {
          run: () => patchTask(t.id, input, opt),
          undo: () => patchTask(t.id, undoInput, undoOpt),
        };
      }));
  };

  const bulkComplete = () => {
    void runAll('Completed', selected.map((t): BulkOp => {
      const snap = snapshotOf(t);
      return {
        run: () => patchTask(t.id, { state: '1_done' }, { state: '1_done' }),
        // suppressNotify mirrors closeWithUndo: undoing a bulk-complete must not
        // fire spurious "Approved" pings or trip content-team auto-advance.
        undo: () => patchTask(t.id, { state: snap.state, suppressNotify: true } as never, { state: snap.state }),
      };
    }));
  };

  return (
    <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 animate-fade-in">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-brand/50 bg-canvas px-3 py-2 shadow-2xl">
        <span className="text-xs font-semibold text-brand">{selected.length} selected</span>
        <select value="" disabled={busy} onChange={(e) => { const v = Number(e.target.value); if (v) void bulkStage(v); }}
          className="rounded-md border border-line bg-page px-1.5 py-1 text-[12px] text-ink outline-none">
          <option value="">Move to stage…</option>
          {(stages ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value="" disabled={busy} onChange={(e) => { const v = Number(e.target.value); if (v) bulkAssign(v); }}
          className="rounded-md border border-line bg-page px-1.5 py-1 text-[12px] text-ink outline-none">
          <option value="">Assign…</option>
          {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <span className="flex items-center gap-1 text-[12px] text-muted">
          <button disabled={busy} onClick={() => bulkShift(-shiftDays)} className="rounded border border-line px-1.5 py-0.5 hover:bg-hover hover:text-ink" title={`Pull dates ${shiftDays} day(s) earlier`}><Icon i={Minus} size={14} /></button>
          <input type="number" min={1} max={365} value={shiftDays} onChange={(e) => setShiftDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
            className="w-11 rounded border border-line bg-page px-1 py-0.5 text-center text-[12px] text-ink outline-none" />
          <span>d</span>
          <button disabled={busy} onClick={() => bulkShift(shiftDays)} className="rounded border border-line px-1.5 py-0.5 hover:bg-hover hover:text-ink" title={`Push dates ${shiftDays} day(s) later`}><Icon i={Plus} size={14} /></button>
        </span>
        <button disabled={busy} onClick={bulkComplete} className="flex items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-[12px] font-semibold text-page hover:brightness-110 disabled:opacity-50">
          {busy ? 'Working…' : <><Icon i={Check} size={14} /> Complete</>}
        </button>
        <button disabled={busy} onClick={clearSelected} className="text-[12px] text-muted hover:text-ink">Clear</button>
      </div>
    </div>
  );
}
